import { ChangeDetectionStrategy, Component, output, OnInit, Signal, computed, input, signal } from '@angular/core';
import { Observable, map } from 'rxjs';
import { TypeCoreComponent } from 'src/app/pages/type-routes/type-core.component.js';

import { FormSaveHandler } from 'lib/ui/form/form-save-button.component.js';
import { PackageDb } from 'lib/cosmos/package/packageDb.js';
import { CosmosDb } from 'lib/cosmos/cosmosDb.js';

@Component({
    selector: 'mod-package-form',
    templateUrl: './package-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PackageFormComponent extends TypeCoreComponent<PackageDb.IPackageItem | undefined> implements OnInit {
	public readonly isInDialog = input<boolean>(false);

	public readonly create = output<PackageDb.IPackageItem>();

	public readonly uploadFunction: (file: File) => Observable<CosmosDb.ICosmosFileReference[]> = (file) => {
		return this.dependencies.httpService
			.uploadFile({
				file: file,
				type: 'package'
			})
			.pipe(
				map((response) => {
					return [...this.form.avatar(), response.data];
				})
			);
	};

	public readonly form = {
		id: signal<string | undefined>(undefined),
		name: signal<string | undefined>(undefined),
		description: signal<string | undefined>(undefined),
		price: signal<string | undefined>(undefined),
		avatar: signal<CosmosDb.ICosmosFileReference[]>([])
	};

	public readonly isEditForm: Signal<boolean> = computed(() => {
		if (this.form.id()) {
			return true;
		}
		return false;
	});

	public readonly saveHandler = computed<FormSaveHandler>(() => {
		const sharedData = this.sharedData();
		const form = this.form;
		const name = form.name();

		if (!name) {
			return undefined;
		}

		return this.dependencies.httpService
			.upsert<PackageDb.IPackageItem, PackageDb.IBasePackageItem>({
				type: 'package',
				existingItem: sharedData?.data,
				createModel: {
					name: name,
					description: form.description(),
					price: form.price(),
					avatar: form.avatar()
				},
				patchModel: (item) => {
					return {
						id: item.id,
						name: name,
						description: form.description(),
						price: form.price(),
						avatar: form.avatar()
					};
				}
			})
			.pipe(
				map((response) => {
					super.notifyUpsert('Package', this.isEditForm());
					this.create.emit(response.data);

					super.updateSharedData({
						data: response.data,
						linkedItems: response.linkedItems
					});

					if (!this.isInDialog() && !this.isEditForm()) {
						this.dependencies.navigateService
							.actionRoute((routes) => routes.packageRoutes.package.children.edit, response.data.id)
							.navigate();
					}
				})
			);
	});

	constructor() {
		super();

		this.sharedDataInit = (sharedData) => {
			if (sharedData?.data) {
				this.form.id.set(sharedData.data.id);
				this.form.name.set(sharedData.data.system.name);
				this.form.description.set(sharedData.data.description);
				this.form.price.set(sharedData.data.price);
				this.form.avatar.set(sharedData.data.avatar ?? []);
			}
		};
	}

	handleUploadImage(files: CosmosDb.ICosmosFileReference[] | undefined): void {
		if (files) {
			this.form.avatar.set(files);
			const saveHandler = this.saveHandler();

			if (saveHandler && this.isEditForm()) {
				super.subscribeToObservable(saveHandler);
			}
		}
	}
}
